import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, Heading, Label, Paragraph, Select, Table, Tag, ValidationMessage } from '@digdir/designsystemet-react';
import type { RiskMatrixOptions, RiskMatrixSettings } from '../../../../api/hms';
import { joinList } from '../../hmsFormat';
import { cellsWithLevel, matrixRows, riskLevel, thresholdsInvalid } from '../riskMatrixView';
import layout from '../../hmsPage.module.css';
import classes from '../HRRiskMatrix.module.css';

type Props = {
  settings: RiskMatrixSettings;
  options: RiskMatrixOptions;
  onChange: (next: Partial<RiskMatrixSettings>) => void;
};

/** The order in the sentence about exceptions */
const EXCEPTION_ORDER = ['HARASSMENT', 'SEXUAL_HARASSMENT', 'VIOLENCE_THREATS'];

const range = (from: number, to: number) => (from === to ? String(from) : `${from}–${to}`);

export const LevelsCard = ({ settings, options, onChange }: Props): ReactElement => {
  const { t } = useTranslation();
  const invalid = thresholdsInvalid(settings);
  const exceptions = EXCEPTION_ORDER.filter((key) => settings.oneReportHighFactorKeys.includes(key)).map((key) =>
    t(`hrRiskMatrix.levels.exceptionNames.${key}`),
  );
  const exceptionList = joinList(exceptions, t, 'or');

  return (
    <section aria-labelledby="hr-rm-levels" className={layout.card}>
      <div className={layout.cardHeading}>
        <span className={layout.step}>{t('hrRiskMatrix.step', { step: 1, total: 4 })}</span>
        <Heading level={2} data-size="sm" id="hr-rm-levels">
          {t('hrRiskMatrix.levels.title')}
        </Heading>
        <Paragraph data-size="sm" className={layout.cardIntro}>
          {t('hrRiskMatrix.levels.intro')}
        </Paragraph>
      </div>
      <div className={classes.matrixRow}>
        <table aria-label={t('hrRiskMatrix.levels.matrixLabel')} className={classes.matrix}>
          <caption className="ds-sr-only">{t('hrRiskMatrix.levels.matrixCaption')}</caption>
          <thead>
            <tr>
              <td />
              <th scope="colgroup" colSpan={5} className={classes.axis}>
                {t('hrRiskMatrix.levels.consequenceAxis')}
              </th>
            </tr>
          </thead>
          <tbody>
            {matrixRows().map((row) => (
              <tr key={row.likelihood}>
                <th scope="row" className={classes.axisRow}>
                  {row.likelihood}
                </th>
                {row.cells.map((cell) => {
                  const level = riskLevel(cell.score, settings);
                  return (
                    <td
                      key={cell.consequence}
                      className={classes.cell}
                      data-level={level}
                      aria-label={t('hrRiskMatrix.levels.cellLabel', {
                        likelihood: row.likelihood,
                        consequence: cell.consequence,
                        score: cell.score,
                        level: t(`hrRiskMatrix.levelNames.${level}`).toLowerCase(),
                      })}
                    >
                      {cell.score}
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr>
              <th scope="row" className={classes.axisLikelihood}>
                {t('hrRiskMatrix.levels.likelihoodAxis')}
              </th>
              {[1, 2, 3, 4, 5].map((consequence) => (
                <td key={consequence} className={classes.axis}>
                  {consequence}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
        <div className={classes.thresholds}>
          <div className={classes.thresholdFields}>
            <Field data-size="sm">
              <Label htmlFor="hr-rm-moderate">{t('hrRiskMatrix.levels.moderateFrom')}</Label>
              <Select
                id="hr-rm-moderate"
                value={settings.moderateFrom}
                aria-invalid={invalid || undefined}
                aria-describedby={invalid ? 'hr-rm-thresholds-error' : undefined}
                onChange={(event) => onChange({ moderateFrom: Number(event.target.value) })}
              >
                {options.moderateFrom.map((option) => (
                  <Select.Option key={option} value={option}>
                    {option}
                  </Select.Option>
                ))}
              </Select>
            </Field>
            <Field data-size="sm">
              <Label htmlFor="hr-rm-high">{t('hrRiskMatrix.levels.highFrom')}</Label>
              <Select
                id="hr-rm-high"
                value={settings.highFrom}
                aria-invalid={invalid || undefined}
                aria-describedby={invalid ? 'hr-rm-thresholds-error' : undefined}
                onChange={(event) => onChange({ highFrom: Number(event.target.value) })}
              >
                {options.highFrom.map((option) => (
                  <Select.Option key={option} value={option}>
                    {option}
                  </Select.Option>
                ))}
              </Select>
            </Field>
          </div>
          {invalid ? (
            <ValidationMessage id="hr-rm-thresholds-error">{t('hrRiskMatrix.levels.thresholdsError')}</ValidationMessage>
          ) : (
            <Paragraph data-size="xs" className={layout.subtle}>
              {t('hrRiskMatrix.levels.summary', {
                low: range(1, settings.moderateFrom - 1),
                moderate: range(settings.moderateFrom, settings.highFrom - 1),
                high: range(settings.highFrom, 25),
                count: cellsWithLevel('high', settings),
                exceptions: exceptionList,
              })}
            </Paragraph>
          )}
        </div>
      </div>
      <div className={layout.tableScroll}>
        <Table data-size="sm" className={classes.levelTable}>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>{t('hrRiskMatrix.levels.level')}</Table.HeaderCell>
              <Table.HeaderCell>{t('hrRiskMatrix.levels.requirement')}</Table.HeaderCell>
              <Table.HeaderCell>{t('hrRiskMatrix.levels.deadline')}</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            <Table.Row>
              <Table.HeaderCell scope="row">
                <Tag data-size="sm" data-color="danger">
                  {t('hrRiskMatrix.levelNames.high')}
                </Tag>
              </Table.HeaderCell>
              <Table.Cell>{t('hrRiskMatrix.levels.highRequirement')}</Table.Cell>
              <Table.Cell>
                <Select
                  data-size="sm"
                  aria-label={t('hrRiskMatrix.levels.highDeadlineLabel')}
                  value={settings.deadlineHighDays}
                  onChange={(event) => onChange({ deadlineHighDays: Number(event.target.value) })}
                >
                  {options.deadlineHighDays.map((days) => (
                    <Select.Option key={days} value={days}>
                      {t('hrRiskMatrix.levels.days', { count: days })}
                    </Select.Option>
                  ))}
                </Select>
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.HeaderCell scope="row">
                <Tag data-size="sm" data-color="warning">
                  {t('hrRiskMatrix.levelNames.moderate')}
                </Tag>
              </Table.HeaderCell>
              <Table.Cell>{t('hrRiskMatrix.levels.moderateRequirement')}</Table.Cell>
              <Table.Cell>
                <Select
                  data-size="sm"
                  aria-label={t('hrRiskMatrix.levels.moderateDeadlineLabel')}
                  value={settings.deadlineModerateDays}
                  onChange={(event) => onChange({ deadlineModerateDays: Number(event.target.value) })}
                >
                  {options.deadlineModerateDays.map((days) => (
                    <Select.Option key={days} value={days}>
                      {t('hrRiskMatrix.levels.days', { count: days })}
                    </Select.Option>
                  ))}
                </Select>
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.HeaderCell scope="row">
                <Tag data-size="sm" data-color="success">
                  {t('hrRiskMatrix.levelNames.low')}
                </Tag>
              </Table.HeaderCell>
              <Table.Cell>{t('hrRiskMatrix.levels.lowRequirement')}</Table.Cell>
              <Table.Cell className={layout.subtle}>
                <span aria-hidden="true">–</span>
                <span className="ds-sr-only">{t('hrRiskMatrix.levels.noDeadline')}</span>
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      </div>
      <Field data-size="sm" className={classes.narrowField}>
        <Label htmlFor="hr-rm-interval">{t('hrRiskMatrix.levels.interval')}</Label>
        <Select
          id="hr-rm-interval"
          value={settings.surveyIntervalMonths}
          onChange={(event) => onChange({ surveyIntervalMonths: Number(event.target.value) })}
        >
          {options.surveyIntervalMonths.map((months) => (
            <Select.Option key={months} value={months}>
              {t('hrRiskMatrix.levels.months', { count: months })}
            </Select.Option>
          ))}
        </Select>
      </Field>
    </section>
  );
};
